import type { AuthoringBlockNode, AuthoringDoc } from '@personalizehq/schemas';
import type { FontToken, Theme } from '@personalizehq/schemas/ir';
import { colorSlug } from './color.js';

export type ThemeTokenFamily = keyof Theme;

export interface ThemeTokenReference {
  family: ThemeTokenFamily;
  token: string;
  /** Stable, human-readable field path for impact dialogs and tests. */
  path: string;
  /** Present for references authored on a block; absent for theme-to-theme references. */
  stepId?: string;
  nodeId?: string;
  /** Which experiment variant the referencing step belongs to; absent = the default steps. A token
   *  used ONLY by a variant is still used (ADR 0015) — this walk is what keeps delete honest. */
  variantKey?: string;
}

type TokenValue<F extends ThemeTokenFamily> = Theme[F][string];

/** Every token edge in the WHOLE mutable document, including repeat templates and fallbacks. */
export function collectThemeTokenReferences(doc: AuthoringDoc): ThemeTokenReference[] {
  const references: ThemeTokenReference[] = [];
  let currentVariantKey: string | undefined;
  const add = (
    family: ThemeTokenFamily,
    token: string,
    path: string,
    stepId?: string,
    nodeId?: string
  ): void => {
    references.push({
      family,
      token,
      path,
      ...(stepId ? { stepId } : {}),
      ...(nodeId ? { nodeId } : {}),
      ...(currentVariantKey ? { variantKey: currentVariantKey } : {}),
    });
  };

  // A button variant COMPOSES foundation tokens (ADR 0014): fg/bg colours, a gradient bg, a `radii`
  // radius, an `elevations` elevation. Each is a "use" of that token.
  for (const [variant, value] of Object.entries(doc.theme.buttons)) {
    add('colors', value.fg, `theme.buttons.${variant}.fg`);
    if (typeof value.bg === 'string') add('colors', value.bg, `theme.buttons.${variant}.bg`);
    else if (value.bg) add('gradients', value.bg.gradient, `theme.buttons.${variant}.bg.gradient`);
    if (typeof value.radius === 'string') add('radii', value.radius, `theme.buttons.${variant}.radius`);
    if (value.elevation) add('elevations', value.elevation, `theme.buttons.${variant}.elevation`);
  }
  // Elevation and gradient tokens carry colour tokens INSIDE them (like buttons), so a colour is
  // "used" when an elevation/gradient references it — deleting it must fix or refuse (ADR 0014).
  for (const [name, elevation] of Object.entries(doc.theme.elevations)) {
    add('colors', elevation.color, `theme.elevations.${name}.color`);
  }
  for (const [name, gradient] of Object.entries(doc.theme.gradients)) {
    gradient.stops.forEach((token, index) => add('colors', token, `theme.gradients.${name}.stops.${index}`));
  }

  // Every step LIST: the default steps, then each variant's — a token referenced only by a variant
  // must still count as used, or deleting it silently orphans that arm (the V1 blind spot).
  const stepLists: Array<{ variantKey?: string; prefix: string; steps: AuthoringDoc['steps'] }> = [
    { prefix: 'steps', steps: doc.steps },
    ...(doc.variants ?? []).map((variant) => ({
      variantKey: variant.key,
      prefix: `variants.${variant.key}.steps`,
      steps: variant.steps,
    })),
  ];
  for (const list of stepLists) {
    currentVariantKey = list.variantKey;
    for (const step of list.steps) {
      walkBlock(step.root, `${list.prefix}.${step.id}.root`, (node, path) => {
        const nodePath = `${path}.${node.id}`;
        const nodeAdd = (family: ThemeTokenFamily, token: string, field: string): void =>
          add(family, token, `${nodePath}.${field}`, step.id, node.id);

        const style = node.style;
        const bg = style?.bg;
        if (typeof bg === 'string') nodeAdd('colors', bg, 'style.bg');
        else if (bg && 'gradient' in bg) nodeAdd('gradients', bg.gradient, 'style.bg.gradient');
        else if (bg) bg.stops.forEach((token, index) => nodeAdd('colors', token, `style.bg.stops.${index}`));

        if (style?.border) nodeAdd('colors', style.border.color, 'style.border.color');

        const shadow = style?.shadow;
        if (typeof shadow === 'string') nodeAdd('elevations', shadow, 'style.shadow');
        else if (shadow) nodeAdd('colors', shadow.color, 'style.shadow.color');

        if (typeof style?.radius === 'string') nodeAdd('radii', style.radius, 'style.radius');

        switch (node.block) {
          case 'text':
            nodeAdd('fonts', node.font, 'font');
            nodeAdd('colors', node.color, 'color');
            break;
          case 'image':
            if (node.tint) nodeAdd('colors', node.tint, 'tint');
            if (typeof node.radius === 'string') nodeAdd('radii', node.radius, 'radius');
            break;
          case 'video':
            if (typeof node.radius === 'string') nodeAdd('radii', node.radius, 'radius');
            break;
          case 'button':
            nodeAdd('buttons', node.variant, 'variant');
            nodeAdd('fonts', node.font, 'font');
            break;
          case 'progress':
            nodeAdd('colors', node.fill_color, 'fill_color');
            nodeAdd('colors', node.track_color, 'track_color');
            break;
        }
      });
    }
  }
  currentVariantKey = undefined;

  return references;
}

export function themeTokenReferences(
  doc: AuthoringDoc,
  family: ThemeTokenFamily,
  token: string
): ThemeTokenReference[] {
  return collectThemeTokenReferences(doc).filter((reference) => reference.family === family && reference.token === token);
}

export function addThemeToken<F extends ThemeTokenFamily>(
  doc: AuthoringDoc,
  family: F,
  name: string,
  value: TokenValue<F>
): string {
  const token = validateNewTokenName(doc.theme, family, name);
  setFamily(doc.theme, family, { ...doc.theme[family], [token]: value });
  return token;
}

/** MINT A COLOUR TOKEN FROM A RAW PAIR — the compensation SPEC §4 promised and the editor never had.
 *
 *  Raw colour is forbidden on the wire because a bare `#FF0000` has no dark variant and dark mode
 *  would silently rot. That constraint is right — it is why the SDK needs no variant-resolution
 *  machinery at all — but until now the author paid its whole cost: six steps through the Theme panel
 *  to use one new colour. Minting is what makes the rule invisible instead of expensive.
 *
 *  DEDUP LIVES HERE, NOT AT THE CALL SITE, so every future caller inherits it: an identical pair
 *  returns the EXISTING token. Forty picks of the same purple make one token, and the inspector, the
 *  theme panel and a future MCP writer cannot disagree about that.
 *
 *  The caller is responsible for doing this inside the SAME `applyChange` as the assignment — ⌘Z must
 *  undo "I used a new colour", not leave an orphan token behind. */
export function mintColorToken(
  doc: AuthoringDoc,
  light: string,
  dark: string,
  requestedName?: string
): string {
  const existing = Object.entries(doc.theme.colors).find(
    ([, pair]) => pair.light.toLowerCase() === light.toLowerCase() && pair.dark.toLowerCase() === dark.toLowerCase()
  );
  if (existing) return existing[0];

  const base = requestedName?.trim() || `color.${colorSlug(light)}`;
  return addThemeToken(doc, 'colors', uniqueTokenName(doc.theme, 'colors', base), { light, dark });
}

/** CSS numeric weights → the words a designer uses. A token called `body_600` is a token nobody can
 *  read back; `body_semibold` is the same information in the vocabulary the field is already in. */
const WEIGHT_NAMES: Readonly<Record<number, string>> = {
  100: 'thin',
  200: 'extralight',
  300: 'light',
  400: 'regular',
  500: 'medium',
  600: 'semibold',
  700: 'bold',
  800: 'extrabold',
  900: 'black',
};

/** What CHANGED, as a name suffix — `font.body` + 18pt → `font.body_18`, + italic → `font.body_italic`.
 *
 *  Ordered size → weight → style → tracking so the same pair of tokens always produces the same name,
 *  whatever order the author edited the fields in. A change this cannot describe (line height alone)
 *  falls through to `uniqueTokenName`'s numeric suffix rather than inventing an unreadable slug. */
function fontVariantSuffix(base: FontToken, next: FontToken): string {
  const parts: string[] = [];
  if (next.size !== base.size) parts.push(String(next.size));
  if (next.weight !== base.weight) parts.push(WEIGHT_NAMES[next.weight] ?? String(next.weight));
  if ((next.style ?? 'normal') !== (base.style ?? 'normal')) parts.push(next.style ?? 'normal');
  const tracking = next.letter_spacing ?? 0;
  // Three names, not two: a return to 0 is neither loose nor tight — calling it `tight` (which
  // `> 0 ? loose : tight` does) names the variant after the opposite of what changed.
  if (tracking !== (base.letter_spacing ?? 0)) {
    parts.push(tracking === 0 ? 'untracked' : tracking > 0 ? 'loose' : 'tight');
  }
  return parts.join('_');
}

/** A NEW TYPE STYLE, DERIVED FROM AN EXISTING ONE — "like `body`, but 18pt".
 *
 *  `text.font` is a token key, so a per-node size would change every block using that token. Rather
 *  than open the field to inline values, this makes the second type style cheap: same family, one
 *  field different, named after what changed, created and assigned in one click.
 *
 *  FAMILY AND `src` ARE NOT VARIABLE. A different family is a different token, not a variant of this
 *  one — and `src` names the bundled bytes (SPEC §4.1), so varying it here would produce a token
 *  whose family and file disagree. `max_scale` is inherited for a safety reason: it is the Dynamic
 *  Type ceiling, and a variant that quietly dropped it ships type that breaks at 2× with nothing in
 *  the panel to say so.
 *
 *  Dedups on VALUE like `mintColorToken`, so deriving the same variant twice reuses the first. */
export function mintFontVariant(
  doc: AuthoringDoc,
  baseToken: string,
  next: FontToken,
  requestedName?: string
): string {
  const existing = Object.entries(doc.theme.fonts).find(
    ([, value]) =>
      value.family === next.family &&
      value.src === next.src &&
      value.size === next.size &&
      value.weight === next.weight &&
      value.line_height === next.line_height &&
      value.max_scale === next.max_scale &&
      (value.style ?? 'normal') === (next.style ?? 'normal') &&
      (value.letter_spacing ?? 0) === (next.letter_spacing ?? 0)
  );
  if (existing) return existing[0];

  const base = doc.theme.fonts[baseToken];
  const suffix = base ? fontVariantSuffix(base, next) : '';
  const name = requestedName?.trim() || (suffix ? `${baseToken}_${suffix}` : `${baseToken} copy`);
  return addThemeToken(doc, 'fonts', uniqueTokenName(doc.theme, 'fonts', name), next);
}

/** Colour tokens no block (and no other token) references. Minting on demand makes orphans possible;
 *  this is what makes them reversible, and it reuses the same reference walk the delete dialog
 *  already trusts rather than inventing a second notion of "used". */
export function unusedColorTokens(doc: AuthoringDoc): string[] {
  const used = new Set(
    collectThemeTokenReferences(doc)
      .filter((reference) => reference.family === 'colors')
      .map((reference) => reference.token)
  );
  return Object.keys(doc.theme.colors).filter((token) => !used.has(token)).sort();
}

export function duplicateThemeToken(
  doc: AuthoringDoc,
  family: ThemeTokenFamily,
  token: string,
  requestedName?: string
): string {
  const value = requireToken(doc.theme, family, token);
  const name = requestedName
    ? validateNewTokenName(doc.theme, family, requestedName)
    : uniqueTokenName(doc.theme, family, `${token} copy`);
  // JSON-compatible Theme values only. A deep clone keeps nested padding independent.
  setFamily(doc.theme, family, { ...doc.theme[family], [name]: cloneThemeValue(value) });
  return name;
}

export function renameThemeToken(
  doc: AuthoringDoc,
  family: ThemeTokenFamily,
  token: string,
  requestedName: string
): string {
  requireToken(doc.theme, family, token);
  const name = requestedName.trim();
  if (!name) throw new Error('Token name is required.');
  if (name === token) return token;
  if (Object.prototype.hasOwnProperty.call(doc.theme[family], name)) {
    throw new Error(`A ${family.slice(0, -1)} token named “${name}” already exists.`);
  }

  const renamed = Object.fromEntries(
    Object.entries(doc.theme[family]).map(([key, entry]) => [key === token ? name : key, entry])
  );
  setFamily(doc.theme, family, renamed);
  rewriteThemeTokenReferences(doc, family, token, name);
  return name;
}

/** Delete unused tokens directly; used tokens must name an existing same-family replacement. */
export function deleteThemeToken(
  doc: AuthoringDoc,
  family: ThemeTokenFamily,
  token: string,
  replacement?: string
): ThemeTokenReference[] {
  requireToken(doc.theme, family, token);
  const references = themeTokenReferences(doc, family, token);
  if (references.length > 0 && !replacement) {
    throw new Error(`The ${family.slice(0, -1)} token “${token}” is still used.`);
  }
  if (replacement) {
    if (replacement === token) throw new Error('Replacement must be a different token.');
    requireToken(doc.theme, family, replacement);
    rewriteThemeTokenReferences(doc, family, token, replacement);
  }

  const next = { ...doc.theme[family] };
  delete next[token];
  setFamily(doc.theme, family, next);
  return references;
}

function rewriteThemeTokenReferences(
  doc: AuthoringDoc,
  family: ThemeTokenFamily,
  token: string,
  replacement: string
): void {
  // Component tokens (button variants) compose foundation tokens — rewrite whichever family changed.
  Object.values(doc.theme.buttons).forEach((variant) => {
    if (family === 'colors') {
      if (typeof variant.bg === 'string' && variant.bg === token) variant.bg = replacement;
      if (variant.fg === token) variant.fg = replacement;
    }
    if (family === 'gradients' && variant.bg && typeof variant.bg !== 'string' && variant.bg.gradient === token) {
      variant.bg = { gradient: replacement };
    }
    if (family === 'radii' && variant.radius === token) variant.radius = replacement;
    if (family === 'elevations' && variant.elevation === token) variant.elevation = replacement;
  });
  if (family === 'colors') {
    // Colour edges INSIDE elevation/gradient tokens (ADR 0014) — the theme-internal half of §10.
    Object.values(doc.theme.elevations).forEach((elevation) => {
      if (elevation.color === token) elevation.color = replacement;
    });
    Object.values(doc.theme.gradients).forEach((gradient) => {
      gradient.stops = gradient.stops.map((stop) => (stop === token ? replacement : stop));
    });
  }

  // Every step list — the default steps AND each variant's (a rename that skipped a variant would
  // leave that arm pointing at a token that no longer exists, the exact V1 blind spot).
  const allSteps = [...doc.steps, ...(doc.variants ?? []).flatMap((variant) => variant.steps)];
  for (const step of allSteps) {
    walkBlock(step.root, '', (node) => {
      if (family === 'colors') rewriteNodeColors(node, token, replacement);
      if (family === 'radii') rewriteNodeRadii(node, token, replacement);
      if (family === 'fonts') {
        if (node.block === 'text' && node.font === token) node.font = replacement;
        if (node.block === 'button' && node.font === token) node.font = replacement;
      }
      if (family === 'buttons' && node.block === 'button' && node.variant === token) {
        node.variant = replacement;
      }
      if (family === 'elevations' && typeof node.style?.shadow === 'string' && node.style.shadow === token) {
        node.style.shadow = replacement;
      }
      if (family === 'gradients') {
        const bg = node.style?.bg;
        if (bg && typeof bg !== 'string' && 'gradient' in bg && bg.gradient === token) bg.gradient = replacement;
      }
    });
  }
}

function rewriteNodeColors(node: AuthoringBlockNode, token: string, replacement: string): void {
  const style = node.style;
  const bg = style?.bg;
  if (typeof bg === 'string') {
    if (bg === token) style!.bg = replacement;
  } else if (bg && !('gradient' in bg)) {
    bg.stops = bg.stops.map((stop) => (stop === token ? replacement : stop));
  }
  if (style?.border?.color === token) style.border.color = replacement;
  const shadow = style?.shadow;
  if (shadow && typeof shadow !== 'string' && shadow.color === token) shadow.color = replacement;

  if (node.block === 'text' && node.color === token) node.color = replacement;
  if (node.block === 'image' && node.tint === token) node.tint = replacement;
  if (node.block === 'progress') {
    if (node.fill_color === token) node.fill_color = replacement;
    if (node.track_color === token) node.track_color = replacement;
  }
}

/** Rewrite `radii` token references — `style.radius` and the media `radius` when they are token keys. */
function rewriteNodeRadii(node: AuthoringBlockNode, token: string, replacement: string): void {
  if (typeof node.style?.radius === 'string' && node.style.radius === token) node.style.radius = replacement;
  if ((node.block === 'image' || node.block === 'video') && typeof node.radius === 'string' && node.radius === token) {
    node.radius = replacement;
  }
}

function walkBlock(
  node: AuthoringBlockNode,
  path: string,
  visit: (node: AuthoringBlockNode, path: string) => void
): void {
  visit(node, path);
  if ('children' in node) node.children.forEach((child, index) => walkBlock(child, `${path}.children.${index}`, visit));
  if (node.block === 'repeat') walkBlock(node.template, `${path}.template`, visit);
  if (node.fallback) walkBlock(node.fallback, `${path}.fallback`, visit);
}

function requireToken(
  theme: Theme,
  family: ThemeTokenFamily,
  token: string
): Theme[ThemeTokenFamily][string] {
  const value = theme[family][token];
  if (!value) throw new Error(`Unknown ${family.slice(0, -1)} token “${token}”.`);
  return value;
}

function validateNewTokenName(theme: Theme, family: ThemeTokenFamily, requested: string): string {
  const name = requested.trim();
  if (!name) throw new Error('Token name is required.');
  if (Object.prototype.hasOwnProperty.call(theme[family], name)) {
    throw new Error(`A ${family.slice(0, -1)} token named “${name}” already exists.`);
  }
  return name;
}

function uniqueTokenName(theme: Theme, family: ThemeTokenFamily, requested: string): string {
  if (!Object.prototype.hasOwnProperty.call(theme[family], requested)) return requested;
  let suffix = 2;
  while (Object.prototype.hasOwnProperty.call(theme[family], `${requested} ${suffix}`)) suffix += 1;
  return `${requested} ${suffix}`;
}

function setFamily(theme: Theme, family: ThemeTokenFamily, value: Record<string, unknown>): void {
  // All three families are string-keyed records; callers preserve their family-specific values.
  (theme as unknown as Record<ThemeTokenFamily, Record<string, unknown>>)[family] = value;
}

function cloneThemeValue<T>(value: T): T {
  // JSON round-trip deliberately supports Immer draft proxies; structuredClone(proxy) throws.
  return JSON.parse(JSON.stringify(value)) as T;
}
